/**
 * useCancelPaymentData — « J'ai mis à jour, recharger » (étape 4).
 *
 * Relit l'aperçu pour récupérer la carte que le parent vient d'enregistrer dans le portail
 * Stripe, sans le faire recommencer le parcours — port littéral de
 * `CancelPaymentScreen.js:109-130` et `:218-222` (même service, sans paramètre).
 *
 * La garde `awaiting` de `useApiFetch` compte ici : l'étape 3, montée dessous, écoute la même
 * tranche et ne doit pas se repeindre sur une relecture demandée ici. C'est aussi pourquoi la
 * tranche est rendue au repos même sur une erreur (`idleOnError`) : une erreur laissée là serait
 * lue par l'étape 3 comme la sienne.
 */
import { useCallback, useReducer } from "react"

import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import {
    type ApiCancellationFees,
    type ApiDefaultPaymentMethod,
} from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { useApiFetch } from "../../../hooks/useApiFetch"

/** `CancelPaymentScreen.js:119-120` — la phrase du legacy, en un seul accusé. */
const NO_CARD_FOUND =
    "Aucune carte trouvée. Nous ne détectons toujours pas de moyen de paiement valide. Vérifiez que la mise à jour est bien enregistrée dans votre portail Stripe."
const RELOAD_FAILED = "La relecture de votre moyen de paiement a échoué. Réessayez dans un instant."

type State = {
    refreshedPm: ApiDefaultPaymentMethod | null
    reloading: boolean
    feedback: string | null
}

type Action =
    | { type: "RELOAD" }
    | { type: "CARD_FOUND"; pm: ApiDefaultPaymentMethod }
    | { type: "NO_CARD" }
    | { type: "FAILED"; message: string }
    | { type: "CLOSE_FEEDBACK" }

const initialState: State = { refreshedPm: null, reloading: false, feedback: null }

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "RELOAD":
            return { ...state, reloading: true, feedback: null }
        case "CARD_FOUND":
            return { refreshedPm: action.pm, reloading: false, feedback: null }
        case "NO_CARD":
            return { ...state, reloading: false, feedback: NO_CARD_FOUND }
        case "FAILED":
            return { ...state, reloading: false, feedback: action.message }
        case "CLOSE_FEEDBACK":
            return { ...state, feedback: null }
        default:
            return state
    }
}

export function useCancelPaymentData() {
    const [state, dispatchUi] = useReducer(reducer, initialState)

    const { fetch } = useApiFetch<ApiCancellationFees>({
        service: subscriptionService.getCancellationFees,
        fetchOnMount: false,
        skipWhilePending: true,
        idleOnError: true,
        onSuccess: (data) => {
            const pm = data?.default_pm
            dispatchUi(pm?.last4 ? { type: "CARD_FOUND", pm } : { type: "NO_CARD" })
        },
        onError: () => dispatchUi({ type: "FAILED", message: RELOAD_FAILED }),
    })

    // L'attente ne s'affiche que si l'appel est bien parti : un second tap pendant le vol ne
    // rallume pas le bouton pour rien.
    const reload = useCallback(() => {
        if (fetch()) dispatchUi({ type: "RELOAD" })
    }, [fetch])

    return {
        refreshedPm: state.refreshedPm,
        reloading: state.reloading,
        feedback: state.feedback,
        reload,
        dismissFeedback: useCallback(() => dispatchUi({ type: "CLOSE_FEEDBACK" }), []),
    }
}
